/**
 * 志望動機書 AI（利用回数・五千兆円相当の生成・課金導線・履歴書自動入力）。
 */
import axios from 'axios'
import { nextTick, ref, type Ref } from 'vue'
import localforage from 'localforage'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { emitter, emitOpenLoginDialog } from '@/plugins/emitter'
import {
  autofillShibouDoukishoAi,
  type RirekishoAutofillSource,
  type ShokurekishoAutofillSource
} from '@/utils/format-edit/autofillShibouDoukishoAi'
import type { ShibouDoukishoFormatData } from '@/composables/format-edit/useShibouDoukishoFormatData'

const RIREKISHO_SAVE_KEY = 'format_rirekisho'
const SHOKUREKISHO_SAVE_KEY = 'format_shokumukeirekisho'
const USAGE_CATEGORY = 'reason_long'
const LOADING_TEXT = 'AIがあなたに最適な志望動機書を生成しています'
const GENERATE_TIMEOUT_MS = 120000

type GlobalVarWithPayments = {
  isYagiPrime: boolean
  in_app_payments?: boolean
}

export type ShibouDoukishoChatBotExpose = {
  open: () => void
  showResultView: (message: unknown) => void
  markCompleted: () => void
}

type AxiosErrorBody = {
  response?: { data?: { message?: string; success?: boolean } }
}

function axiosErrorMessage(error: unknown): string | undefined {
  return (error as AxiosErrorBody).response?.data?.message
}

export function useShibouDoukishoAi(formatData: ShibouDoukishoFormatData) {
  const { varuna, message, cookies, globalVar } = useHostServices()
  const { user, isAuthenticated } = useAuth()
  const gVar = globalVar as typeof globalVar & GlobalVarWithPayments

  const chatBot = ref<ShibouDoukishoChatBotExpose | null>(null)
  const fullscreenLoading = ref(false)
  const loadingText = ref(LOADING_TEXT)
  const aiUsageCount = ref(0)
  const aiMaxUsageCount = ref(1)

  function openReasonLongPaymentDialog() {
    const routeId = cookies.get('_YAGISH_YAGIPRIME_ROUTE_ID') || ''
    cookies.set?.(
      '_YAGISH_YAGIPRIME_ROUTE_ID',
      routeId + '#reason_long',
      null,
      null,
      'yagish.jp'
    )
    cookies.set?.(
      '_YAGISH_YAGIPRIME_ROUTE_ID',
      routeId + '#reason_long',
      null,
      null,
      'localhost'
    )
    emitter.emit('show-yagiprime-payment-dialog', 'reason_long')
  }

  async function fetchAiUsageCount() {
    if (!isAuthenticated.value || !user.value?.uid) {
      aiUsageCount.value = 0
      aiMaxUsageCount.value = 1
      return
    }
    const body = new FormData()
    body.append('uuid', user.value.uid)
    body.append('category_type', USAGE_CATEGORY)
    try {
      const response = await axios.post(varuna.endPoints.getAiUsageCount, body)
      if (response.data.success) {
        aiUsageCount.value = response.data.usage_count || 0
        aiMaxUsageCount.value = gVar.isYagiPrime ? 10 : 1
      }
    } catch (error) {
      console.error('AI利用回数の取得に失敗しました', error)
    }
  }

  async function autoFillFromRirekisho() {
    try {
      const rirekisho = (await localforage.getItem(
        RIREKISHO_SAVE_KEY
      )) as RirekishoAutofillSource | null
      const shokurekisho = (await localforage.getItem(
        SHOKUREKISHO_SAVE_KEY
      )) as ShokurekishoAutofillSource | null
      const patch = autofillShibouDoukishoAi({ rirekisho, shokurekisho })
      Object.assign(formatData, patch)
    } catch (err) {
      console.error(err)
    }
  }

  async function getAIMypr() {
    if (!isAuthenticated.value) {
      emitOpenLoginDialog()
      return
    }
    if (!gVar.isYagiPrime && aiUsageCount.value >= 1) {
      openReasonLongPaymentDialog()
      return
    }

    fullscreenLoading.value = true
    const body = new FormData()
    if (user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
    body.append('education_detail', formatData.ai_education_detail || '')
    body.append('licenses_detail', formatData.ai_licenses_detail || '')
    body.append('job_detail', formatData.ai_job_detail || '')
    body.append('company_name', formatData.ai_company_name || '')
    body.append('company_industry', formatData.ai_company_industry || '')
    body.append('company_job', formatData.ai_company_job || '')
    body.append('company_values', formatData.ai_company_values || '')
    body.append('future_vision', formatData.ai_future_vision || '')
    body.append('strength_episode', formatData.ai_strength_episode || '')
    body.append('reason', formatData.longreason || '')

    try {
      const response = await axios.post(
        varuna.endPoints.aiIntroduceReasonLong,
        body,
        { timeout: GENERATE_TIMEOUT_MS }
      )
      if (response.data.success) {
        chatBot.value?.showResultView(response.data.message)
        message({
          message: '取得しました',
          type: 'success'
        })
        await fetchAiUsageCount()
      } else {
        message({
          message: '失敗しました',
          type: 'error'
        })
      }
    } catch (error) {
      const errorMessage = axiosErrorMessage(error)
      if (errorMessage === 'AIの使用回数が上限に達しました。') {
        openReasonLongPaymentDialog()
      } else if (errorMessage) {
        message({
          message: errorMessage,
          type: 'error'
        })
      } else {
        message({
          message: '失敗しました',
          type: 'error'
        })
      }
      await fetchAiUsageCount()
    } finally {
      fullscreenLoading.value = false
    }
  }

  function generateAiMypr() {
    getAIMypr()
  }

  function applyAiResult(result: string, close: () => void) {
    formatData.longreason = result.replace(/<br>/gi, '\n')
    chatBot.value?.markCompleted()
    nextTick(() => {
      close()
    })
  }

  function openReasonLongChat() {
    fetchAiUsageCount().finally(() => {
      chatBot.value?.open()
    })
  }

  return {
    chatBot: chatBot as Ref<ShibouDoukishoChatBotExpose | null>,
    fullscreenLoading,
    loadingText,
    aiUsageCount,
    aiMaxUsageCount,
    globalVar: gVar,
    fetchAiUsageCount,
    autoFillFromRirekisho,
    generateAiMypr,
    applyAiResult,
    openReasonLongChat,
    goYagiprimeRegister: openReasonLongPaymentDialog
  }
}
